import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface PixverseV4I2vParams {
  /**
   * 单个图像URL或Base64编码
   */
  img_id?: string;
  /**
   * 多个图像URL或Base64编码的数组
   */
  img_ids?: string[];
  /**
   * 视频时长，5或10秒
   * @default 5
   * @values 5 | 10
   */
  duration?: 5 | 10;
  /**
   * 视频质量
   * @default "540p"
   * @values 270p | 540p | 720p | 1080p
   */
  quality?: '270p' | '540p' | '720p' | '1080p';
  /**
   * 运动模式
   * @default "normal"
   * @values normal | fast
   */
  motion_mode?: 'normal' | 'fast';
  /**
   * 正向提示词
   */
  prompt?: string;
  /**
   * 负向提示词
   */
  negative_prompt?: string;
  /**
   * 摄像机运动
   * @values zoom_in | zoom_out | pan_left | pan_right | tilt_up | tilt_down
   */
  camera_movement?: 'zoom_in' | 'zoom_out' | 'pan_left' | 'pan_right' | 'tilt_up' | 'tilt_down';
  /**
   * 随机种子，用于可重现的生成结果
   */
  seed?: number;
  /**
   * 模板ID
   */
  template_id?: number;
  /**
   * 是否开启音效
   */
  sound_effect_switch?: boolean;
  /**
   * 音效内容
   */
  sound_effect_content?: string;
  /**
   * 是否开启唇形同步TTS
   */
  lip_sync_tts_switch?: boolean;
  /**
   * 唇形同步TTS内容
   */
  lip_sync_tts_content?: string;
  /**
   * 唇形同步TTS说话人ID
   */
  lip_sync_tts_speaker_id?: string;
}

export interface PixverseV4I2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * pixverse_v4_i2v
 *
 * PixVerse V4 图像生成视频接口
 * 
 * Model: v4 (固定模型)
 * Features: 图像生成视频功能
 * Duration: 5秒或10秒（默认5秒）
 * Quality: 270p、540p（默认）、720p、1080p
 * Motion Mode: normal（默认）、fast（快速）
 * Advanced: 支持摄像机运动控制、正向/负向提示词、音效、唇形同步TTS
 * 
 * API Documentation: https://docs.platform.pixverse.ai/image-to-video-generation-13016633e0
 *
 * @example
 * ```typescript
 * import { initSeacloud, pixverseV4I2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await pixverseV4I2v({
 *   // 参数见 PixverseV4I2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 PixverseV4I2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function pixverseV4I2v(
  params: PixverseV4I2vParams,
): Promise<PixverseV4I2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'pixverse_v4_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: PixverseV4I2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
